/* POSHRADS — product.js
   1. Variant selection: posts the selected option value ids to Shopify's Section Rendering API
      (?option_values=…&section_id=…), swaps every [data-product-part], keeps ?variant= (or ?option_values=
      for a combination with no variant) in the URL,
      announces price/availability, and brings the variant's media into view.
      Combined-listing values with their own product_url navigate to that product.
   2. Product recommendations: fills [data-recommendations] from Shopify's recommendations endpoint.
   3. 3D models: loads Shopify's model-viewer UI only when a model is on the page.
   No dependencies. One set of delegated listeners, registered once. */
(() => {
  'use strict';

  if (window.poshProductReady) return;
  window.poshProductReady = true;

  let controller = null;
  const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

  const selectedValueElements = (picker) =>
    [...picker.querySelectorAll('input:checked, select')].map((el) => (el.tagName === 'SELECT' ? el.selectedOptions[0] : el));

  const renderVariant = async (section, picker, changed) => {
    const changedValue = changed.tagName === 'SELECT' ? changed.selectedOptions[0] : changed;
    const productUrl = section.dataset.productUrl;

    if (changedValue.dataset.productUrl && changedValue.dataset.productUrl !== productUrl) {
      window.location.href = changedValue.dataset.productUrl;
      return;
    }

    const ids = selectedValueElements(picker)
      .map((el) => el.dataset.optionValueId)
      .join(',');

    const fallbackUrl = `${productUrl}?option_values=${ids}`;
    if (controller) controller.abort();
    const current = (controller = new AbortController());
    section.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(`${productUrl}?option_values=${ids}&section_id=${section.dataset.sectionId}`, {
        signal: controller.signal,
      });
      const html = response.ok ? new DOMParser().parseFromString(await response.text(), 'text/html') : null;
      const next = html && html.querySelector('[data-product-section]');
      // Never leave the picker and the form's variant id out of sync: fall back to a full page render.
      if (!next) {
        window.location.href = fallbackUrl;
        return;
      }

      const focusedId = document.activeElement && document.activeElement.id;
      // Keep the shopper's quantity when the new variant's rules still allow it.
      const quantityInput = section.querySelector('input[name="quantity"]');
      const previousQuantity = quantityInput ? quantityInput.value : null;

      section.querySelectorAll('[data-product-part]').forEach((part) => {
        const fresh = next.querySelector(`[data-product-part="${part.dataset.productPart}"]`);
        if (fresh) part.replaceWith(fresh);
      });

      if (focusedId) {
        const target = document.getElementById(focusedId);
        if (target) target.focus();
      }

      // min / max / step (Shopify's min and max are multiples of the increment) via the input's own validity.
      const nextQuantity = section.querySelector('input[name="quantity"]');
      if (nextQuantity && previousQuantity) {
        const rendered = nextQuantity.value;
        nextQuantity.value = previousQuantity;
        if (!nextQuantity.validity.valid) nextQuantity.value = rendered;
      }

      const live = section.querySelector('[data-product-live]');
      const nextLive = next.querySelector('[data-product-live]');
      if (live && nextLive) live.textContent = nextLive.textContent;

      // The URL always matches the picker: the variant, or the requested combination when none exists.
      const variantId = next.dataset.variantId;
      section.dataset.variantId = variantId;
      const query = variantId ? `variant=${variantId}` : `option_values=${ids}`;
      window.history.replaceState(window.history.state, '', `${productUrl}?${query}`);

      // Mobile: bring the variant's media into the swipe strip without scrolling the page.
      // Desktop: the gallery is a static grid; the page is not moved.
      const media = next.dataset.mediaId && section.querySelector(`[data-media-id="${next.dataset.mediaId}"]`);
      const strip = media && media.parentElement;
      if (media && strip && strip.scrollWidth > strip.clientWidth) {
        strip.scrollTo({ left: media.offsetLeft - strip.offsetLeft, behavior: REDUCED_MOTION.matches ? 'auto' : 'smooth' });
      }
    } catch (error) {
      if (error.name !== 'AbortError') window.location.href = fallbackUrl;
    } finally {
      if (controller === current) section.removeAttribute('aria-busy');
    }
  };

  document.addEventListener('change', (event) => {
    const picker = event.target.closest('[data-variant-picker]');
    if (!picker) return;
    const section = picker.closest('[data-product-section]');
    if (section) renderVariant(section, picker, event.target);
  });

  // Recommendations are fetched only when their container nears the viewport.
  const fetchRecommendations = async (container) => {
    if (container.dataset.loaded) return;
    container.dataset.loaded = 'true';
    try {
      const response = await fetch(container.dataset.url);
      if (!response.ok) return;
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const fresh = html.querySelector('[data-recommendations]');
      if (fresh && fresh.innerHTML.trim()) container.innerHTML = fresh.innerHTML;
    } catch (error) {
      // Recommendations are supplementary: on failure the section simply stays empty.
    }
  };

  const observer =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) =>
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              observer.unobserve(entry.target);
              fetchRecommendations(entry.target);
            }),
          { rootMargin: '600px 0px' }
        )
      : null;

  const loadRecommendations = (scope) => {
    scope.querySelectorAll('[data-recommendations][data-url]').forEach((container) => {
      if (observer) observer.observe(container);
      else fetchRecommendations(container);
    });
  };

  // Each model gets the Shopify UI once (marked), and only models inside the (re)loaded scope are touched.
  const loadModels = (scope) => {
    const pending = () => scope.querySelectorAll('.product-media model-viewer:not([data-model-ui])');
    if (!pending().length || !window.Shopify || !window.Shopify.loadFeatures) return;
    window.Shopify.loadFeatures([
      {
        name: 'model-viewer-ui',
        version: '1.0',
        onLoad: (error) => {
          if (error) return;
          pending().forEach((model) => {
            model.dataset.modelUi = 'true';
            new window.Shopify.ModelViewerUI(model);
          });
        },
      },
    ]);
  };

  const init = (scope) => {
    loadRecommendations(scope);
    loadModels(scope);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document));
  else init(document);

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    if (observer) event.target.querySelectorAll('[data-recommendations]').forEach((target) => observer.unobserve(target));
  });
})();
